import { router } from '@inertiajs/react'
import type CrmCoreUser from '@/types/serializers/CrmCoreUser'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { StatusBadge } from './StatusBadge'
import { TeamMembershipsManager } from './TeamMembershipsManager'

interface UserDetailSheetProps {
  user: CrmCoreUser | null
  teams: { id: number; name: string }[]
  onOpenChange: (open: boolean) => void
  onTransfer?: (user: CrmCoreUser) => void
  onRemove?: (user: CrmCoreUser) => void
}

export function UserDetailSheet({
  user,
  teams,
  onOpenChange,
  onTransfer,
  onRemove,
}: UserDetailSheetProps) {
  function handleCancelInvite() {
    if (!user) return
    router.delete(`/admin/users/${user.id}/cancel_invite`, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  const formattedDate = user?.joined_at
    ? new Date(user.joined_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—'

  return (
    <Sheet open={user !== null} onOpenChange={onOpenChange}>
      <SheetContent className="w-[540px] sm:max-w-[540px]">
        <SheetHeader>
          <SheetTitle>{user?.name ?? 'User Details'}</SheetTitle>
          <SheetDescription>View user account information.</SheetDescription>
        </SheetHeader>

        {user && (
          <div className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Name</p>
                <p className="text-sm">{user.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Email</p>
                <p className="text-sm">{user.email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Role</p>
                <p className="text-sm">
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1).replace('_', ' ')}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <StatusBadge status={user.status} />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Joined</p>
                <p className="text-sm">{formattedDate}</p>
              </div>
            </div>

            <Separator />

            <TeamMembershipsManager user={user} teams={teams} />

            <Separator />

            <div className="flex gap-2">
              {user.status === 'invited' && (
                <Button variant="destructive" onClick={handleCancelInvite}>
                  Cancel Invite
                </Button>
              )}
              {user.status === 'active' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => onTransfer?.(user)}
                  >
                    Transfer to Team
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => onRemove?.(user)}
                  >
                    Remove
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
